import React, { useEffect, useMemo, useState } from "react";
import { JobPost } from "@/types/jobPost";
import { useJobPostDetails } from "@/hooks";
import { JobPostContext } from "./contexts";

interface JobPostProviderProps {
  children: React.ReactNode;
}

export function JobPostProvider({ children }: JobPostProviderProps) {
  /* --------------------- States --------------------- */
  const [jobPostDetailsModalOpen, setJobPostDetailsModalOpen] = useState(false);

  const [showAppliedModal, setShowAppliedModal] = useState(false);
  const [recentlyAppliedJobId, setRecentlyAppliedJobId] = useState<
    JobPost["id"] | undefined
  >(undefined);

  const [flagJobPostModalOpen, setFlagJobPostModalOpen] = useState(false);

  const [jobPost, setJobPost] = useState<JobPost | null>(null);

  const [jobPostId, setJobPostId] = useState<JobPost["id"] | undefined>(
    undefined,
  );

  /* --------------------- Hooks --------------------- */
  const {
    jobPost: jobPostDetails,
    loading: jobPostDetailsLoading,
    error: jobPostDetailsError,
  } = useJobPostDetails({ jobPostId: jobPostId || recentlyAppliedJobId });

  const closeFlagJobPostModal = () => {
    setFlagJobPostModalOpen(false);
    setJobPostId(undefined);
    setJobPost(null);
  };

  const closeJobPostDetailsModal = () => {
    setJobPostDetailsModalOpen(false);
    setJobPostId(undefined);
    setJobPost(null);
  };

  const closeAppliedModal = () => {
    setShowAppliedModal(false);
    setRecentlyAppliedJobId(undefined);
    setJobPost(null);
  };

  /* --------------------- Constants --------------------- */
  const loading = jobPostDetailsLoading; // just in case we need to add more loading states later
  const error = jobPostDetailsError;

  /* --------------------- Effects --------------------- */

  useEffect(() => {
    if (
      (jobPostDetails && jobPostId) ||
      (jobPostDetails && recentlyAppliedJobId)
    ) {
      setJobPost(jobPostDetails);
    }
  }, [jobPostDetails]);

  const value = useMemo(
    () => ({
      flagJobPostModalOpen,
      setFlagJobPostModalOpen,
      jobPost,
      loading,
      error,
      jobPostId,
      setJobPostId,
      closeFlagJobPostModal,
      showAppliedModal,
      setShowAppliedModal,
      recentlyAppliedJobId,
      setRecentlyAppliedJobId,
      jobPostDetailsModalOpen,
      setJobPostDetailsModalOpen,
      closeJobPostDetailsModal,
      closeAppliedModal,
    }),
    [
      flagJobPostModalOpen,
      setFlagJobPostModalOpen,
      jobPost,
      loading,
      error,
      setJobPostId,
      closeFlagJobPostModal,
      showAppliedModal,
      setShowAppliedModal,
      recentlyAppliedJobId,
      setRecentlyAppliedJobId,
      jobPostDetailsModalOpen,
      setJobPostDetailsModalOpen,
      closeJobPostDetailsModal,
      closeAppliedModal,
    ],
  );

  return (
    <JobPostContext.Provider value={value}>{children}</JobPostContext.Provider>
  );
}
